import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../ui/tooltip";
// --- Added CornerDownRight for the refund indicator ---
import { ShieldCheck, ShieldX, Clock, CornerDownRight } from 'lucide-react';

// --- Helper function to format time difference ---
const formatDuration = (milliseconds) => {
  if (milliseconds < 0) return "N/A";
  if (milliseconds === 0) return "0s";

  let totalSeconds = Math.floor(milliseconds / 1000);
  
  const days = Math.floor(totalSeconds / 86400);
  totalSeconds %= 86400; // seconds remaining in a day
  
  const hours = Math.floor(totalSeconds / 3600);
  totalSeconds %= 3600; // seconds remaining in an hour
  
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);
  
  return parts.join(' ');
};

export const columns = ({ onRefundClick, canReturnCoins }) => [
  {
    accessorKey: "transaction_date",
    header: "Date",
    cell: ({ row }) => {
      const date = new Date(row.original.transaction_date);
      return (
        <div className="font-medium">
          {date.toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })}
          <div className="text-xs text-muted-foreground">
            {date.toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit', hour12: true })}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "description",
    header: "Details",
    cell: ({ row }) => {
      // --- Extracted the new refund fields ---
      const {
        description,
        lead_category_name,
        lead_phone_verified,
        lead_created_at,
        lead_purchased_at,
        lead_id,
        is_refunded,
        refunded_by_admin_email,
        refund_reason,
        zd_ticket_id,
        refunded_at,
        lead_purchase_id,
        original_price_coins,
        is_discounted,
        discount_label,
      } = row.original;

      const isLead = row.original.transaction_id?.startsWith("lead-");

      const basePath = process.env.REACT_APP_STAGE === 'prod' ? 'https://sanctum.selyst.com' : 'https://dev-sanctum.selyst.com'

      const buyerLeadUrl = `${basePath}/admin/lead/${lead_id}`;
      
      // Calculate time to purchase
      const timeToPurchase = lead_created_at && lead_purchased_at 
        ? new Date(lead_purchased_at) - new Date(lead_created_at)
        : null;
      
      console.log("Refunded At:", refunded_at);
      console.log("Purchased at:",lead_purchased_at);
        const timeToRefund = lead_purchased_at && is_refunded ? new Date(refunded_at) - new Date(lead_purchased_at) : null;


      const refunded_at_date = new Date(refunded_at);
      const refund_date_ist = refunded_at_date?.toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) || null;

      return (
        <div className="flex flex-col gap-1">
          {isLead && lead_id ? (
            <a
              href={buyerLeadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-blue-600 hover:underline"
            >
              {description} (Purchase ID: {lead_purchase_id})
            </a>
          ) : (
            <span className="text-sm font-medium">{description}</span>
          )}
          {isLead && lead_category_name && (
            <span className="text-xs text-muted-foreground">{lead_category_name}</span>
          )}
          {isLead && is_discounted && (
            <Badge variant="outline" className="w-fit text-emerald-700 border-emerald-300 text-[10px] font-normal">
              Was {original_price_coins} coins{discount_label ? ` · ${discount_label}` : ''}
            </Badge>
          )}
          {isLead && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
              {lead_phone_verified ? (
                  <span className="flex items-center gap-1 text-green-600">
                    <ShieldCheck size={14} /> Verified
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-red-600">
                    <ShieldX size={14} /> Not Verified
                  </span>
              )}
              {timeToPurchase !== null && (
                 <span className="flex items-center gap-1">
                    <Clock size={14} /> {formatDuration(timeToPurchase)}
                 </span>
              )}
            </div>
          )}

          {/* --- NEW SECTION: Display Admin Refund Details --- */}
          {is_refunded && refunded_by_admin_email && row.original.transaction_type != 'CREDIT' && (
            <div className="mt-2 flex flex-col gap-1 rounded bg-muted/50 p-2 text-xs border border-border/50">
              <div className="flex items-center gap-1 font-medium text-muted-foreground">
                <CornerDownRight size={12} />
                Refunded by: {refunded_by_admin_email}
              </div>
              {refund_reason && (
                <div className="pl-4 text-muted-foreground italic line-clamp-2" title={refund_reason}>
                  Reason: {refund_reason}
                </div>
              )}
              {zd_ticket_id && (
                <div className="pl-4 text-muted-foreground italic line-clamp-2" title={refund_reason}>
                  Zoho Desk Ticket ID: {zd_ticket_id}
                </div>
              )}
              {refunded_at && (
                <div className="pl-4 text-muted-foreground italic line-clamp-2" title={refund_reason}>
                  Refunded at IST: {refund_date_ist} <span>(After {formatDuration(timeToRefund)} of purchase)</span>
                </div>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  {
    id: "change",
    header: "Change",
    cell: ({ row }) => {
      const isCredit = row.original.transaction_type === 'CREDIT';
      return (
        <Badge variant={isCredit ? "success" : "destructive"} className="font-mono">
          {row.original.change_amount} {row.original.change_unit}
        </Badge>
      );
    },
  },
  {
    id: "balance",
    header: "Balance After",
    cell: ({ row }) => (
      <div>
        <span className="font-semibold">{row.original.coin_balance_after}</span>
        <span className="text-muted-foreground"> Coins</span>
        <div className="text-xs">
          <span className="font-semibold">{row.original.response_balance_after}</span>
          <span className="text-muted-foreground"> Free</span>
        </div>
      </div>
    ),
  },
  canReturnCoins && {
    id: "actions",
    cell: ({ row }) => {
      const { transaction_id, is_refunded } = row.original;
      const isLeadPurchase = transaction_id?.startsWith("lead-");
      
      if (!isLeadPurchase) return null;

      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRefundClick(row.original)}
                  disabled={is_refunded}
                >
                  {is_refunded ? "Refunded" : "Return Coins"}
                </Button>
              </div>
            </TooltipTrigger>
            {is_refunded && (
                <TooltipContent>
                    <p>This purchase has already been refunded.</p>
                </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      );
    },
  },
].filter(Boolean);